import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Table, Empty, Button, Modal, Spin, message } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import { searchInvoicePayments } from 'store/vendorBillApprovalSlice';
import { getSignedUrl } from 'store/commonSlice';

const HistoryCard = styled(Box)`
	background-color: #fff;
	border-radius: 8px;
	border-top: 2px solid #2f8ecb;
	margin-bottom: 16px;
	padding: 24px;
`;

const SubTitle = styled(Text)`
	font-size: 20px;
	font-weight: 400;
	line-height: 30px;
	margin-bottom: 16px;
	display: block;
`;

const RemainingBalanceContainer = styled.div`
	margin-top: 24px;
	padding-top: 16px;
	border-top: 1px solid #e8e8e8;
`;

const RemainingBalanceLabel = styled(Text)`
	font-size: 16px;
	font-weight: 500;
	color: #333;
	margin-right: 8px;
`;

const RemainingBalanceValue = styled(Text)`
	font-size: 24px;
	font-weight: 700;
	color: #ff9800;
`;

const DocumentPreviewModal = styled(Modal)`
	.ant-modal-body {
		text-align: center;
		padding: 24px;
		background: #f5f5f5;
	}
`;

const PreviewImage = styled.img`
	max-width: 100%;
	max-height: 70vh;
	width: auto;
	height: auto;
	object-fit: contain;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	background: #fff;
	display: block;
	margin: 0 auto;
`;

const PreviewIframe = styled.iframe`
	width: 100%;
	height: 600px;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	background: #fff;
`;

const ImageContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 400px;
	background: #f5f5f5;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	padding: 20px;
	overflow: auto;
`;

function PaymentHistoryTable({ vendorBillId, useV2API = false, vendorBillRes }) {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const [paymentHistory, setPaymentHistory] = useState([]);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [imageError, setImageError] = useState(false);
	const [loadingSignedUrl, setLoadingSignedUrl] = useState(false);

	const fetchPaymentHistory = useCallback(async () => {
		setLoading(true);
		try {
			const action = searchInvoicePayments({
				vendor_bill_id: vendorBillId,
				useV2API,
			});

			const response = await dispatch(action);
			const history = response?.data || response?.rows || [];
			setPaymentHistory(Array.isArray(history) ? history : []);
		} catch (error) {
			console.error('Error fetching payment history:', error);
			setPaymentHistory([]);
		} finally {
			setLoading(false);
		}
	}, [vendorBillId, useV2API, dispatch]);

	useEffect(() => {
		if (vendorBillId) {
			fetchPaymentHistory();
		}
	}, [vendorBillId, fetchPaymentHistory]);

	const getFileExtension = (url = '') => {
		if (!url) return '';
		const cleanUrl = url.split('?')[0];
		const parts = cleanUrl.split('.');
		if (parts.length <= 1) return '';
		return parts[parts.length - 1].toLowerCase();
	};

	const isLikelyImage = (url = '') => {
		if (!url) return false;
		const lower = url.toLowerCase();
		return (
			lower.includes('image') ||
			lower.includes('.png') ||
			lower.includes('.jpg') ||
			lower.includes('.jpeg') ||
			lower.includes('.gif') ||
			lower.includes('.webp')
		);
	};

	const handleViewDocument = async (fileKey, fileName) => {
		if (!fileKey) {
			message.error('No document key available');
			return;
		}

		setLoadingSignedUrl(true);
		setImageError(false);
		setPreviewTitle(fileName || 'Document Preview');
		setPreviewVisible(true);

		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'invoice_payment',
						keys: [fileKey],
					},
				})
			);

			const viewUrl =
				(signedUrlResponse &&
					signedUrlResponse.status === 'success' &&
					signedUrlResponse.data?.[0]?.url) ||
				(signedUrlResponse?.data &&
					Array.isArray(signedUrlResponse.data) &&
					signedUrlResponse.data.length > 0 &&
					signedUrlResponse.data[0]?.url)
					? signedUrlResponse.data[0].url
					: null;

			if (viewUrl) {
				setPreviewUrl(viewUrl);
			} else {
				console.error('Failed to get view URL', signedUrlResponse);
				message.error('Failed to load document preview');
				setImageError(true);
				setPreviewUrl('');
			}
		} catch (error) {
			console.error('Error viewing document:', error);
			message.error('Failed to load document preview');
			setImageError(true);
			setPreviewUrl('');
		} finally {
			setLoadingSignedUrl(false);
		}
	};

	const handleDownloadDocument = async (fileKey, fileName) => {
		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'download',
					data: {
						entity: 'invoice_payment',
						keys: [fileKey],
					},
				})
			);

			const downloadUrl = signedUrlResponse?.data?.[0]?.url;
			if (downloadUrl) {
				const link = document.createElement('a');
				link.href = downloadUrl;
				link.download = fileName || 'document';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				console.error('Failed to get download URL');
			}
		} catch (error) {
			console.error('Error downloading document:', error);
		}
	};

	const renderPreviewContent = () => {
		if (loadingSignedUrl) {
			return (
				<div style={{ padding: '40px', textAlign: 'center' }}>
					<Spin size="large" tip="Loading document..." />
				</div>
			);
		}

		if (!previewUrl) {
			return (
				<div style={{ padding: '40px', textAlign: 'center' }}>
					<Text type="secondary">No document URL available</Text>
				</div>
			);
		}

		const fileExt = getFileExtension(previewUrl);
		const isPdf = fileExt === 'pdf';
		const isImage =
			['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt) || isLikelyImage(previewUrl);

		const isS3Image = previewUrl.includes('amazonaws.com') && !isPdf;

		if (isPdf) {
			return (
				<div>
					<div style={{ marginBottom: '16px' }}>
						<Button
							type="primary"
							onClick={() => window.open(previewUrl, '_blank')}
							size="large"
						>
							Open PDF in New Tab
						</Button>
					</div>
					<PreviewIframe
						title={previewTitle}
						src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
					/>
				</div>
			);
		}

		if (isImage || isS3Image) {
			if (imageError) {
				return (
					<ImageContainer>
						<div style={{ padding: '20px' }}>
							<p style={{ marginBottom: '16px', color: '#ff4d4f' }}>
								Failed to load image. Please try opening in a new tab.
							</p>
							<Button
								type="primary"
								onClick={() => window.open(previewUrl, '_blank')}
							>
								Open in New Tab
							</Button>
						</div>
					</ImageContainer>
				);
			}

			return (
				<ImageContainer>
					<PreviewImage
						src={previewUrl}
						alt={previewTitle}
						onError={() => {
							setImageError(true);
						}}
						onLoad={() => {
							setImageError(false);
						}}
					/>
				</ImageContainer>
			);
		}

		return (
			<ImageContainer>
				<PreviewImage
					src={previewUrl}
					alt={previewTitle}
					onError={() => {
						setImageError(true);
					}}
					onLoad={() => {
						setImageError(false);
					}}
				/>
				{imageError && (
					<div style={{ padding: '20px', marginTop: '20px' }}>
						<p style={{ marginBottom: '16px', color: '#ff4d4f' }}>
							Failed to load image. Please try opening in a new tab.
						</p>
						<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
							Open in New Tab
						</Button>
					</div>
				)}
			</ImageContainer>
		);
	};

	// Helper to parse numeric value (handles string and number)
	const parseNumericValue = (value, defaultValue = 0) => {
		if (value === null || value === undefined || value === '') return defaultValue;
		if (typeof value === 'number') return value;
		if (typeof value === 'string') {
			const parsed = parseFloat(value);
			return Number.isNaN(parsed) ? defaultValue : parsed;
		}
		return defaultValue;
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === '') return '-';
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		return `₹${numValue.toLocaleString('en-IN', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	};

	// Use API value directly from vendor_bill
	const vendorBill = vendorBillRes?.vendor_bill || {};
	const remainingBalance = parseNumericValue(vendorBill?.pending_amount, 0);
	const previousBillPayments = Array.isArray(vendorBill?.previous_bill_payments)
		? vendorBill.previous_bill_payments
		: [];

	const columns = [
		{
			title: 'Amount',
			dataIndex: 'paid_amount',
			key: 'paid_amount',
			render: (value) => <Text>{formatCurrency(value || 0)}</Text>,
		},
		{
			title: 'UTR No',
			dataIndex: 'utr_number',
			key: 'utr_number',
			render: (text) => <Text>{text || '-'}</Text>,
		},
		{
			title: 'Paid At',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (date) => {
				if (!date) return <Text>-</Text>;
				return <Text>{moment(date).format('DD-MM-YYYY h:mm A')}</Text>;
			},
		},
		{
			title: 'Paid By',
			dataIndex: 'created_by_name',
			key: 'created_by_name',
			render: (text) => <Text>{text || '-'}</Text>,
		},
		{
			title: 'Docs',
			dataIndex: 'image_keys',
			key: 'image_keys',
			render: (imageKeys) => {
				// image_keys is an array of S3 key strings
				if (!Array.isArray(imageKeys) || imageKeys.length === 0) {
					return <Text>-</Text>;
				}

				return (
					<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
						{imageKeys.map((fileKey, idx) => {
							const fileName = fileKey.split('-').slice(5).join('-') || fileKey;
							return (
								<div
									key={fileKey}
									style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
								>
									<Button
										type="link"
										icon={<EyeOutlined />}
										onClick={() => handleViewDocument(fileKey, fileName)}
										title={`View doc ${idx + 1}`}
										size="small"
									/>
									<Button
										type="link"
										icon={<DownloadOutlined />}
										onClick={() => handleDownloadDocument(fileKey, fileName)}
										title={`Download doc ${idx + 1}`}
										size="small"
									/>
								</div>
							);
						})}
					</div>
				);
			},
		},
	];

	const previousBillColumns = [
		{
			title: 'Amount',
			dataIndex: 'paid_amount',
			key: 'paid_amount',
			render: (value) => <Text>{formatCurrency(value || 0)}</Text>,
		},
		{
			title: 'UTR No',
			dataIndex: 'utr_number',
			key: 'utr_number',
			render: (text) => <Text>{text || '-'}</Text>,
		},
		{
			title: 'Paid Date',
			dataIndex: 'paid_date',
			key: 'paid_date',
			render: (date) => {
				if (!date) return <Text>-</Text>;
				// Backend sends "MM/DD/YYYY HH:mm:ss" – moment will parse and format for display
				return <Text>{moment(date).format('DD-MM-YYYY h:mm A')}</Text>;
			},
		},
		{
			title: 'Version',
			dataIndex: 'version',
			key: 'version',
			render: (value) => <Text>{value ?? '-'}</Text>,
		},
		{
			title: 'Docs',
			dataIndex: 'image_keys',
			key: 'previous_image_keys',
			render: (imageKeys) => {
				// image_keys is an array of S3 key strings
				if (!Array.isArray(imageKeys) || imageKeys.length === 0) {
					return <Text>-</Text>;
				}

				return (
					<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
						{imageKeys.map((fileKey, idx) => {
							const fileName = fileKey.split('-').slice(5).join('-') || fileKey;
							return (
								<div
									key={fileKey}
									style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
								>
									<Button
										type="link"
										icon={<EyeOutlined />}
										onClick={() => handleViewDocument(fileKey, fileName)}
										title={`View doc ${idx + 1}`}
										size="small"
									/>
									<Button
										type="link"
										icon={<DownloadOutlined />}
										onClick={() => handleDownloadDocument(fileKey, fileName)}
										title={`Download doc ${idx + 1}`}
										size="small"
									/>
								</div>
							);
						})}
					</div>
				);
			},
		},
	];

	return (
		<>
			<HistoryCard>
				<SubTitle>Payment History</SubTitle>
				<Table
					columns={columns}
					dataSource={paymentHistory}
					loading={loading}
					rowKey={(record, index) => record.id || index}
					pagination={false}
					locale={{
						emptyText: (
							<Empty
								description={<Text type="secondary">No payment history found</Text>}
							/>
						),
					}}
				/>
				<RemainingBalanceContainer>
					<RemainingBalanceLabel>Remaining Balance:</RemainingBalanceLabel>
					<RemainingBalanceValue>
						{formatCurrency(remainingBalance)}
					</RemainingBalanceValue>
				</RemainingBalanceContainer>
			</HistoryCard>

			{previousBillPayments.length > 0 && (
				<HistoryCard>
					<SubTitle>Previous Bill Payments</SubTitle>
					<Table
						columns={previousBillColumns}
						dataSource={previousBillPayments}
						rowKey={(record, index) => record.utr_number || index}
						pagination={false}
					/>
				</HistoryCard>
			)}

			<DocumentPreviewModal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={() => {
					setPreviewVisible(false);
					setPreviewUrl('');
					setPreviewTitle('');
					setImageError(false);
				}}
				centered
				width={getFileExtension(previewUrl) === 'pdf' ? 1000 : 800}
				destroyOnClose
			>
				{renderPreviewContent()}
			</DocumentPreviewModal>
		</>
	);
}

PaymentHistoryTable.propTypes = {
	vendorBillId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	useV2API: PropTypes.bool,
	vendorBillRes: PropTypes.object,
};

export default PaymentHistoryTable;
